import { parseArgs } from 'node:util';

import {
  GpmPayAuthenticationError,
  GpmPayConfigError,
  GpmPayConnectionError,
  GpmPayError,
  GpmPayTimeoutError,
} from '../core/errors.js';
import { VERSION } from '../version.js';
import { accountsCommand } from './commands/accounts.js';
import { pingCommand } from './commands/ping.js';
import { simulateCommand } from './commands/simulate.js';
import { transactionsCommand } from './commands/transactions.js';
import { webhookCommand } from './commands/webhook.js';
import { EXIT, err, out, style } from './ui.js';

/**
 * Built on demand, not at module load: `--no-color` sets `NO_COLOR` while this
 * module is already imported, and `style.*` reads that env var per call. A
 * top-level template literal would bake the ANSI codes in before the flag is
 * ever seen.
 */
const helpText = (): string => `
${style.bold('gpmpay')} — CLI for the GPM Pay API  ${style.dim(`v${VERSION}`)}

${style.bold('Usage')}
  gpmpay <command> [options]

${style.bold('Commands')}
  ping                        Validate the API token and show its real scopes
  accounts list               List your bank accounts — where --account comes from
  accounts get <id>           Show one bank account
  transactions list           List recent transactions
  simulate tx                 Fake an inbound transfer (non-production only)
  webhook send --url <url>    Sign a sample payload and POST it to your handler
  webhook listen              Local receiver that verifies signatures live
  webhook verify              Verify one signature (body from --file or stdin)
  webhook settings            List registered webhook endpoints
  webhook history             Recent delivery attempts
  webhook retry <id>          Re-queue a failed delivery

${style.bold('Global options')}
  --token <gpm_...>           Overrides GPMPAY_API_TOKEN
  --base-url <url>            Point at another API host
  --sandbox                   Use https://sandbox-api.gpmpay.com
  --json                      Machine-readable output (secrets redacted)
  --no-color                  Disable ANSI colours
  -h, --help                  Show this help
  -v, --version               Show the SDK version

${style.bold('Command options')}
  accounts list    [--status <ACTIVE|SUSPENDED|PENDING_VERIFICATION>] [--limit <n>]
  transactions     [--limit <n>] [--account <uuid>] [--type <IN|OUT>]
  simulate tx      --account <uuid> --amount <vnd> --content <text>
                   [--type <IN|OUT>] [--allow-production]
  webhook send     --url <url> [--secret <s>] [--amount <vnd>] [--content <text>]
                   [--file <body.json>] [--skew <seconds>] [--bad-signature]
  webhook settings [--driver <HTTP|TELEGRAM|WORDPRESS|GOOGLE_SHEETS>] [--limit <n>]
  webhook history  [--status <PENDING|DELIVERED|RETRYING|FAILED>]
                   [--setting <id>] [--limit <n>]
  webhook verify   --secret <s> --signature "t=..,v1=.." [--file <body.json>]
  webhook listen   --secret <s> [--port <n>]

${style.bold('Environment')}
  GPMPAY_API_TOKEN            Your API token (every command except "webhook send")
  GPMPAY_WEBHOOK_SECRET       Webhook signing secret (webhook send/verify/listen)

${style.dim('Create a token at https://app.gpmpay.com/api-tokens')}
`.trimStart();

const OPTIONS = {
  token: { type: 'string' },
  'base-url': { type: 'string' },
  sandbox: { type: 'boolean' },
  json: { type: 'boolean' },
  // Registered under its literal name. `node:util.parseArgs` has no `--no-`
  // negation, so declaring `color` here made `--no-color` an "Unknown option"
  // in every position while four docs and --help advertised it.
  'no-color': { type: 'boolean' },
  help: { type: 'boolean', short: 'h' },
  version: { type: 'boolean', short: 'v' },
  account: { type: 'string' },
  amount: { type: 'string' },
  desc: { type: 'string' },
  limit: { type: 'string' },
  type: { type: 'string' },
  secret: { type: 'string' },
  file: { type: 'string' },
  signature: { type: 'string' },
  port: { type: 'string' },
  status: { type: 'string' },
  driver: { type: 'string' },
  setting: { type: 'string' },
  url: { type: 'string' },
  content: { type: 'string' },
  event: { type: 'string' },
  skew: { type: 'string' },
  'bad-signature': { type: 'boolean' },
  'allow-production': { type: 'boolean' },
} as const;

function exitCodeFor(error: unknown): number {
  if (error instanceof GpmPayAuthenticationError) return EXIT.AUTH;
  if (error instanceof GpmPayConnectionError) return EXIT.CONNECTION;
  if (error instanceof GpmPayTimeoutError) return EXIT.CONNECTION;
  if (error instanceof GpmPayConfigError) return EXIT.USAGE;
  return EXIT.ERROR;
}

export async function run(argv: string[]): Promise<number> {
  // Applied before parsing, so the help printed on a *parse failure* is plain
  // too — that is the one path where the parsed flag is not available yet.
  if (argv.includes('--no-color')) process.env.NO_COLOR = '1';

  let parsed;
  try {
    parsed = parseArgs({
      args: argv,
      options: OPTIONS,
      allowPositionals: true,
      strict: true,
    });
  } catch (error) {
    err(`${style.red('✗')} ${(error as Error).message}`);
    err();
    err(helpText());
    return EXIT.USAGE;
  }

  const { values, positionals } = parsed;

  if (values.version === true) {
    out(VERSION);
    return EXIT.OK;
  }
  // An explicit --help is a successful request; a bare invocation is a usage error.
  if (values.help === true) {
    out(helpText());
    return EXIT.OK;
  }
  if (positionals.length === 0) {
    out(helpText());
    return EXIT.USAGE;
  }

  const flags = {
    token: values.token,
    baseUrl: values['base-url'],
    sandbox: values.sandbox,
    json: values.json,
    account: values.account,
    amount: values.amount,
    desc: values.desc,
    limit: values.limit,
    type: values.type,
    secret: values.secret,
    file: values.file,
    signature: values.signature,
    port: values.port,
    status: values.status,
    driver: values.driver,
    setting: values.setting,
    url: values.url,
    content: values.content,
    event: values.event,
    skew: values.skew,
    badSignature: values['bad-signature'],
    allowProduction: values['allow-production'],
  };

  const [command, subcommand, ...rest] = positionals;

  switch (command) {
    case 'ping':
      return await pingCommand(flags);
    case 'accounts':
    case 'bank-accounts':
      return await accountsCommand(subcommand ?? 'list', rest, flags);
    case 'transactions':
    case 'tx':
      return await transactionsCommand(subcommand ?? 'list', flags);
    case 'simulate':
    case 'sim':
      // `tx` here is the simulator; a bare `tx` at position 0 is transactions.
      return await simulateCommand(subcommand, flags);
    case 'webhook':
    case 'webhooks':
      return await webhookCommand(subcommand, rest, flags);
    case 'help':
      out(helpText());
      return EXIT.OK;
    default:
      err(`${style.red('✗')} Unknown command "${command ?? ''}".`);
      err();
      err(helpText());
      return EXIT.USAGE;
  }
}

export async function main(argv = process.argv.slice(2)): Promise<number> {
  try {
    return await run(argv);
  } catch (error) {
    const code = exitCodeFor(error);

    if (GpmPayError.isGpmPayError(error)) {
      err(`${style.red('✗')} ${error.message}`);
      if (error instanceof GpmPayAuthenticationError) {
        err(
          style.dim(
            `  request id: ${error.requestId} — quote this when contacting support.`,
          ),
        );
      }
    } else {
      err(`${style.red('✗')} ${(error as Error).message}`);
    }

    return code;
  }
}
